import { useQuery } from '@tanstack/react-query'

import { accessRefused, api, unwrap } from '../../api/client'
import type { components } from '../../api/schema'
import { useSessionEra } from '../../documents/held'
import { t } from '../../i18n'
import { Pending, formatDateTime } from '../../ui'
import { formatLogDate } from './dates'
import { useSealedRead } from './sealed'

type ForecastOut = components['schemas']['ForecastOut']

/**
 * Today's forecast for the project's site, as the weather source words it.
 *
 * Read for the screen and kept nowhere: it is not the day's record and never
 * reaches the day, its report or the event log. One read is shared by the
 * overview card and today's day, and is not asked again for half an hour.
 */
export function useForecast(projectId: string, era: number, enabled = true) {
  return useQuery({
    queryKey: forecastKey(projectId, era),
    enabled,
    staleTime: 30 * 60 * 1000,
    retry: (failures, error) => !accessRefused(error) && failures < 1,
    queryFn: async () =>
      unwrap(
        await api.GET('/dailylog/forecast', { params: { query: { project_id: projectId } } }),
      ),
  })
}

/**
 * The project and the session are both in the key: the next person to sign in
 * at this browser is asked afresh, and one job's forecast is never another's.
 */
export function forecastKey(projectId: string, era: number) {
  return ['dailylog', 'forecast', projectId, era]
}

/**
 * The period's own name and the date it starts on where the site is, so a
 * "Tonight" read after dark, or the next day's period, is not taken for today.
 */
function periodLabel(period: ForecastOut['periods'][number]): string {
  const date = /^\d{4}-\d{2}-\d{2}/.exec(period.starts_at)?.[0]
  return date ? t('dailylog.forecast.period', { name: period.name, date: formatLogDate(date) }) : period.name
}

export function ForecastPeriods({ forecast }: { forecast: ForecastOut }) {
  return (
    <>
      <ul className="feed">
        {forecast.periods.map((period) => (
          <li key={`${period.name}:${period.starts_at}`}>
            <strong>{periodLabel(period)}</strong>
            {period.temperature_f !== null
              ? ` · ${t(period.is_daytime ? 'dailylog.forecast.high' : 'dailylog.forecast.low', {
                  temp: period.temperature_f,
                })}`
              : ''}
            <div>{period.short_forecast}</div>
            {period.wind ? <span className="when">{t('dailylog.forecast.wind', { wind: period.wind })}</span> : null}
          </li>
        ))}
      </ul>
      <p className="muted">
        {t('dailylog.forecast.from', {
          provider: forecast.provider_name ?? '—',
          when: formatDateTime(forecast.retrieved_at),
        })}
      </p>
    </>
  )
}

/**
 * The overview's card. Offered only to somebody who can open the daily log
 * on this job; the screen around it decides that, as it does for the others.
 */
export function ForecastCard({ projectId }: { projectId: string }) {
  const era = useSessionEra()
  const forecast = useForecast(projectId, era)
  // A refusal puts the forecast away, and an answer from before it does not
  // bring it back; see `useSealedRead`.
  const { data: shown, sealed } = useSealedRead(forecast, forecastKey(projectId, era))
  return (
    <section className="card" aria-labelledby="forecast-heading">
      <h3 id="forecast-heading">{t('home.forecast')}</h3>
      {sealed ? <p className="muted">{t('dailylog.forecast.withheld')}</p> : null}
      {!sealed && forecast.isPending ? <Pending /> : null}
      {!sealed && forecast.error ? <p className="muted">{t('dailylog.forecast.not_available')}</p> : null}
      {shown && !shown.available ? (
        <p className="muted">
          {shown.reason === 'unconfigured'
            ? t('dailylog.forecast.no_source')
            : shown.reason === 'incomplete'
              ? t('dailylog.forecast.source_incomplete')
              : t('dailylog.forecast.not_available')}
        </p>
      ) : null}
      {shown?.available ? (
        <>
          <p className="muted">{t('dailylog.forecast.label')}</p>
          {shown.for_date ? (
            <p className="muted">{t('dailylog.forecast.for_date', { date: formatLogDate(shown.for_date) })}</p>
          ) : null}
          <ForecastPeriods forecast={shown} />
        </>
      ) : null}
    </section>
  )
}
